import React from 'react';
import { useTranslation } from 'react-i18next';
import heroMountains from '../assets/hero-mountains.png';
import { s } from '../helpers';

export interface HeaderSelectOption {
  v: string;
  l: string;
}

export interface HeaderSelectField {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options?: HeaderSelectOption[];
}

export interface HeroHeaderProps {
  hdrNet: HeaderSelectField;
  hdrRange: HeaderSelectField;
  heroPad: string;
  heroSize: string;
  heroAlign: string;
  heroText: string;
  heroRightW: string;
  heroSelW: string;
  heroSelWrap: string;
  heroSelFlexA: string;
  heroSelFlexB: string;
  heroQuoteDisp: string;
  todayText: string;
  timeText: string;
  periodText: string;
  lastUpdatedText: string;
  refresh: () => void;
  refreshColor: string;
  refreshLabel: string;
}

export const HeroHeader: React.FC<HeroHeaderProps> = ({
  hdrNet,
  hdrRange,
  heroPad,
  heroSize,
  heroAlign,
  heroText,
  heroRightW,
  heroSelW,
  heroSelWrap,
  heroSelFlexA,
  heroSelFlexB,
  heroQuoteDisp,
  todayText,
  timeText,
  periodText,
  lastUpdatedText,
  refresh,
  refreshColor,
  refreshLabel
}) => {
  const { t } = useTranslation();

  return (
    <div
      style={s(
        `position:relative;overflow:hidden;background:#FCF8F0 url('${heroMountains}') no-repeat 35% 30% / cover;border:1px solid #EFE6D6;border-radius:12px;padding:${heroPad};display:flex;flex-wrap:wrap;gap:12px 24px;align-items:flex-start;justify-content:space-between`
      )}
    >
      <div style={s(`display:flex;flex-direction:column;gap:8px;min-width:0;border-left:2px solid #C99A4E;padding-left:16px`)}>
        <span style={s(`font-size:11.5px;letter-spacing:0.18em;color:#6B6358;text-transform:uppercase`)}>
          {t('managerNetwork.header.badge', 'BEQ Capital Manager Network')}
        </span>
        <h1
          style={s(
            `margin:0;font-family:'Cormorant Garamond',Georgia,serif;font-size:${heroSize};line-height:1.1;font-weight:700;color:#A67A2E;text-transform:uppercase;letter-spacing:0.01em;text-wrap:pretty`
          )}
        >
          {t('managerNetwork.header.title', 'Kiến tạo những nhà quản lý vốn xuất sắc')}
        </h1>
        <span style={s(`font-size:11.5px;letter-spacing:0.22em;color:#6B6358;text-transform:uppercase`)}>
          {t('managerNetwork.header.subtitle', 'Kỷ luật hôm nay · Giá trị bền vững ngày mai')}
        </span>
      </div>
      <div style={s(`display:flex;flex-direction:column;align-items:${heroAlign};gap:8px;margin-left:auto;width:${heroRightW}`)}>
        <div style={s(`text-align:${heroText};font-size:12.5px;color:#3A342C;line-height:1.5`)}>
          {todayText}
          <br />
          {timeText} (GMT+7)
        </div>
        <div
          style={s(
            `font-family:'Cormorant Garamond',Georgia,serif;font-style:italic;font-size:19px;color:#8C6424;line-height:1.15;display:${heroQuoteDisp};gap:8px`
          )}
        >
          <span style={s(`font-size:28px;line-height:0.8`)}>“</span>
          <span>
            {t('managerNetwork.header.quotePart1', 'Better People')}
            <br />
            {t('managerNetwork.header.quotePart2', 'Stronger Tomorrow')}
          </span>
        </div>
        <div style={s(`display:flex;flex-wrap:${heroSelWrap};gap:10px;justify-content:${heroAlign};width:${heroSelW}`)}>
          <label
            style={s(
              `position:relative;display:flex;align-items:center;height:40px;background:#fff;border:1px solid #E6DCCB;border-radius:8px;padding:0 10px;flex:${heroSelFlexA};min-width:0`
            )}
          >
            <i className="icon-globe" style={s(`color:#3A342C;font-size:15px`)}></i>
            <select
              value={hdrNet.value}
              onChange={hdrNet.onChange}
              style={s(
                `border:0;outline:0;background:transparent;font-size:13px;color:#1B1B1B;padding:0 26px 0 10px;cursor:pointer;flex:1;min-width:0`
              )}
            >
              {hdrNet.options?.map((o, o_idx) => (
                <React.Fragment key={o_idx}>
                  <option value={o.v}>{o.l}</option>
                </React.Fragment>
              ))}
            </select>
            <i className="icon-chevron-down" style={s(`position:absolute;right:10px;color:#6B6358;pointer-events:none`)}></i>
          </label>
          <label
            style={s(
              `position:relative;display:flex;align-items:center;height:40px;background:#fff;border:1px solid #E6DCCB;border-radius:8px;padding:0 10px;flex:${heroSelFlexB};min-width:0`
            )}
          >
            <i className="icon-calendar" style={s(`color:#3A342C;font-size:15px`)}></i>
            <select
              value={hdrRange.value}
              onChange={hdrRange.onChange}
              style={s(
                `border:0;outline:0;background:transparent;font-size:13px;color:#1B1B1B;padding:0 26px 0 10px;cursor:pointer;flex:1;min-width:0`
              )}
            >
              {hdrRange.options?.map((o, o_idx) => (
                <React.Fragment key={o_idx}>
                  <option value={o.v}>{o.l}</option>
                </React.Fragment>
              ))}
            </select>
            <i className="icon-chevron-down" style={s(`position:absolute;right:10px;color:#6B6358;pointer-events:none`)}></i>
          </label>
        </div>
        <div style={s(`display:flex;flex-wrap:wrap;align-items:center;gap:8px;font-size:12px;color:#6B6358`)}>
          <span>{periodText}</span>
          <span style={s(`width:1px;height:12px;background:#E0D5C2`)}></span>
          <span>
            {t('managerNetwork.header.lastUpdated', 'Last updated:')}{' '}
            <b style={s(`color:#1B1B1B;font-weight:600`)}>{lastUpdatedText}</b>
          </span>
          <button
            onClick={refresh}
            title={t('managerNetwork.header.refresh', 'Refresh')}
            style={s(
              `height:28px;padding:0 10px;border:1px solid #E6DCCB;border-radius:6px;background:#fff;color:#1B1B1B;display:flex;align-items:center;gap:5px;font-size:12px;cursor:pointer`
            )}
          >
            <i className="icon-refresh-cw" style={s(`color:${refreshColor}`)}></i>
            {refreshLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
